import { getFirestore, doc, setDoc, getDoc, updateDoc, increment, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { firebaseConfig } from '../../firebase.config';
import { initializeApp, getApp } from 'firebase/app';

// Get existing Firebase app or initialize if it doesn't exist
let app;
try {
  app = getApp();
} catch (error) {
  app = initializeApp(firebaseConfig);
}

const db = getFirestore(app);
const auth = getAuth(app);
const functions = getFunctions(app);

class ReferralService {
  // Generate referral link for current user
  async generateReferralLink() {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Placeholder App Store link for App Store review
      // This will be replaced with the actual App Store URL after app approval
      // Format: https://apps.apple.com/app/id[APP_ID]?referrer=${user.uid}
      // Note for reviewers: This is a placeholder link indicating where the App Store listing will be
      const referralLink = `App Store link pending approval (Referrer ID: ${user.uid})`;
      return referralLink;
    } catch (error) {
      throw error;
    }
  }

  // Share referral link using native sharing (Web Share API)
  async shareReferralLink() {
    try {
      const referralLink = await this.generateReferralLink();
      
      // For web, use the Web Share API if available
      if (navigator.share) {
        const result = await navigator.share({
          title: 'Join SpacedAI with me!',
          text: `Join me on SpacedAI! Use my referral link to get 50 free AI flashcards. App Store link will be available after app approval.`,
          url: referralLink,
        });
        return { action: 'shared', result };
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(`Join me on SpacedAI! Use my referral link to get 50 free AI flashcards. ${referralLink}`);
        alert('Referral link copied to clipboard!');
        return { action: 'copied' };
      }
    } catch (error) {
      // If sharing is canceled or fails, just return canceled
      if (error.name === 'AbortError') {
        return { action: 'dismissed' };
      }
      throw error;
    }
  }

  // Process referral when new user signs up
  async processReferral(referrerId, newUserId) {
    try {
      if (!referrerId || !newUserId) {
        throw new Error('Referrer ID and new user ID are required');
      }

      // Create referral record
      const referralData = {
        referrer_id: referrerId,
        new_user_id: newUserId,
        status: 'pending',
        created_at: new Date(),
        processed_at: null,
      };

      // Add referral record to Firestore
      const referralRef = await addDoc(collection(db, 'referrals'), referralData);

      // Update user documents with referral info
      await this.updateUserReferralInfo(referrerId, newUserId);

      // Trigger Cloud Function to process rewards
      await this.triggerReferralRewards(referralRef.id);

      return referralRef.id;
    } catch (error) {
      throw error;
    }
  }

  // Update user documents with referral information
  async updateUserReferralInfo(referrerId, newUserId) {
    try {
      // Update referrer's document
      const referrerRef = doc(db, 'users', referrerId);
      await updateDoc(referrerRef, {
        referrals_count: increment(1),
        last_referral_at: new Date(),
      });

      // Update new user's document
      const newUserRef = doc(db, 'users', newUserId);
      await updateDoc(newUserRef, {
        referred_by: referrerId,
        referral_bonus_claimed: false,
      });

    } catch (error) {
      throw error;
    }
  }

  // Trigger Cloud Function to process referral rewards
  async triggerReferralRewards(referralId) {
    try {
      const processReferralRewards = httpsCallable(functions, 'processReferralRewards');
      const result = await processReferralRewards({ referralId });
      
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  // Get referral statistics for current user
  async getReferralStats() {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const getReferralStatsCallable = httpsCallable(functions, 'getReferralStats');
      const result = await getReferralStatsCallable();
      
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  // Check if user was referred by someone
  async checkUserReferral() {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const checkUserReferral = httpsCallable(functions, 'checkUserReferral');
      const result = await checkUserReferral();
      
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  // Extract referrer ID from URL parameters
  // Handles both web URLs and deep links (e.g., spacedai://register?ref=USER_ID)
  extractReferrerFromUrl(url) {
    try {
      // Handle deep links (spacedai://register?ref=USER_ID)
      if (url.includes('://')) {
        const parts = url.split('?');
        if (parts.length > 1) {
          const params = parts[1];
          const paramPairs = params.split('&');
          for (const pair of paramPairs) {
            const [key, value] = pair.split('=');
            if (key === 'ref' || key === 'referrer') {
              return decodeURIComponent(value);
            }
          }
        }
      }
      
      // Handle standard URLs
      const urlObj = new URL(url);
      const referrerId = urlObj.searchParams.get('ref') || urlObj.searchParams.get('referrer');
      return referrerId;
    } catch (error) {
      // If URL parsing fails, try manual parsing
      try {
        const match = url.match(/[?&](?:ref|referrer)=([^&]+)/);
        if (match && match[1]) {
          return decodeURIComponent(match[1]);
        }
      } catch (e) {
        // Silently fail
      }
      return null;
    }
  }
}

export default new ReferralService();
